import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import { ElevatedButton } from '@producer/ui-kit';

import { DeleteProjectModal } from '../modals/delete-project.modal';
import { Project } from '../../../app/entities';
import { tabPool } from '../../tabs/models/tabs-pool';
import { archiveProject, unarchiveProject } from '../store/projects.slice';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { catchError } from '../../../core/catch-error';

import styles from './danger-zone.module.scss';

type DangerZoneProps = {
  project?: Project;
};

export const DangerZone: React.FC<DangerZoneProps> = observer(({ project }) => {
  const { spaceId } = useParams() as { spaceId: string };

  const navigate = useNavigate();
  const currentMember = useCurrentMember();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);

  const canArchive = useMemo(() => {
    if (!currentMember) return false;
    if (currentMember.role === 'guest') return false;
    if (['owner', 'admin'].includes(currentMember.role)) return true;
    // Check if user is project creator
    return project?.createdBy === currentMember.userId;
  }, [currentMember, project]);

  const handleOnDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleArchiveToggle = async () => {
    if (!project) {
      return;
    }
    setIsArchiving(true);
    try {
      if (project.archived) {
        await unarchiveProject(project._id);
      } else {
        await archiveProject(project._id);
      }
    } catch (e) {
      catchError(e);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleSuccess = () => {
    if (!project) {
      return;
    }
    const href = `/space/${spaceId}`;

    const tab = tabPool.elements.find((tab) => tab.key === project?._id);
    if (tab) {
      tab.close();
    }
    navigate(href);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles['header']}>Danger Zone</h2>

      <div className={styles.content}>
        <div className={styles['settings-card']}>
          <div className={styles['card-content']}>
            <div className={styles['title-wrapper']}>
              <h3>Transfer ownership</h3>
              <div className={styles['coming-soon']}>Coming soon</div>
            </div>

            <p>Transfer this project to another space.</p>
          </div>

          <ElevatedButton variant="default" text="Transfer" disabled />
        </div>

        <div className={styles['settings-card']}>
          <div className={styles['card-content']}>
            <h3>{project?.archived ? 'Unarchive this project' : 'Archive this project'}</h3>
            <p>
              {project?.archived
                ? 'Restore this project so it becomes editable again.'
                : 'Mark this project as archived and read-only.'}
            </p>
          </div>

          <ElevatedButton
            variant="default"
            onClick={handleArchiveToggle}
            disabled={!canArchive || isArchiving}
            text={project?.archived ? 'Unarchive this project' : 'Archive this project'}
          />
        </div>

        <div className={styles['settings-card']}>
          <div className={styles['card-content']}>
            <h3>Delete this project</h3>
            <p>Once you delete a project, there is no coming back. Please be certain.</p>
          </div>

          <ElevatedButton
            variant="danger"
            onClick={handleOnDeleteClick}
            text="Delete this project"
          />
        </div>
      </div>

      {project && isDeleteModalOpen && (
        <DeleteProjectModal
          onCancel={() => setIsDeleteModalOpen(false)}
          onSuccess={handleSuccess}
          projectId={project?._id}
        />
      )}
    </div>
  );
});
